import axios from 'axios'
import {chargeBackHost} from 'GLOBAL/envVariables'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {cooperStatus, OptionItem} from '../func/whetherStatus'

type MultiSelectProps = {
  officeValue: string
  onChange: (values: string[]) => void
  type?: 'option' | 'cooper'
}

const MultiSelect = ({officeValue, onChange, type = 'option'}: MultiSelectProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null)

  const [selectedOptions, setSelectedOptions] = useState<string[]>([])
  const [open, setOpen] = useState(false)
  const [options, setOptions] = useState<OptionItem[]>([])

  const getLabel = useCallback((item: string | OptionItem) => {
    return typeof item === 'string' ? item : item.label
  }, [])

  const getValue = useCallback((item: string | OptionItem) => {
    return typeof item === 'string' ? item : item.value
  }, [])

  const optionValues = useMemo(() => options.map(getValue), [options, getValue])

  useEffect(() => {
    onChange(selectedOptions)
  }, [selectedOptions, onChange])

  useEffect(() => {
    const applyOptions = (nextOptions: OptionItem[]) => {
      setOptions(nextOptions)

      setSelectedOptions((prev) => {
        const prevValid = prev.filter((val) => nextOptions.some((opt) => getValue(opt) === val))

        if (prevValid.length > 0) return prevValid

        const hasAll = nextOptions.some((o) => getValue(o) === 'all')

        return hasAll ? ['all'] : []
      })
    }

    /**
     * A800 제조사 목록 조회
     */
    const fetchManufacturerOptions = async () => {
      try {
        const res = await axios.get(`${chargeBackHost}/api/manufacturer/select`, {
          params: {
            office_id: officeValue,
          },
        })

        if (!res.data?.success) {
          setOptions([])
          setSelectedOptions([])
          return
        }

        const apiOptions: OptionItem[] = (res.data.data || [])
          .filter((item: any) => item.used_status === true)
          .sort((a: any, b: any) => Number(a.cooper_idx) - Number(b.cooper_idx))
          .map((item: any) => ({
            label: item.cooper_name,
            value: String(item.cooper_idx),
          }))

        const fetchedOptions: OptionItem[] =
          apiOptions.length > 0
            ? [
                {
                  label: '전체',
                  value: 'all',
                },
                ...apiOptions,
              ]
            : []

        applyOptions(fetchedOptions)
      } catch (err) {
        console.error('제조사 조회 실패:', err)

        setOptions([])
        setSelectedOptions([])
      }
    }

    /**
     * 검침원 목록 조회
     */
    const fetchMeterReaderOptions = async () => {
      try {
        const res = await axios.get(`${chargeBackHost}/api/gumdam/select`, {
          params: {
            office_id: officeValue,
          },
        })

        if (!res.data?.success) {
          setOptions([])
          setSelectedOptions([])
          return
        }

        const apiOptions: OptionItem[] = (res.data.data || [])
          .filter((item: any) => item.used_status === true)
          .map((item: any) => ({
            label: item.gum_name,
            value: item.gum_code,
          }))

        const fetchedOptions: OptionItem[] =
          apiOptions.length > 0
            ? [
                {
                  label: '전체',
                  value: 'all',
                },
                ...apiOptions,
                {
                  label: '미할당',
                  value: 'no',
                },
              ]
            : []

        applyOptions(fetchedOptions)
      } catch (err) {
        console.error('검침원코드 조회 실패:', err)

        setOptions([])
        setSelectedOptions([])
      }
    }

    const fetchOptions = async () => {
      if (!officeValue) {
        setOptions([])
        setSelectedOptions([])
        return
      }

      /**
       * 제조사 필터
       */
      if (type === 'cooper') {
        /**
         * A800은 제조사 관리 API 사용
         */
        if (officeValue === 'A800') {
          await fetchManufacturerOptions()
          return
        }

        /**
         * 기존 사업소는 cooperStatus 하드코딩 값 사용
         */
        const matched = cooperStatus.find((item: any) => item.office === officeValue)

        if (matched?.whether) {
          applyOptions(matched.options)
        } else {
          setOptions([])
          setSelectedOptions([])
        }

        return
      }

      /**
       * 검침원 필터
       */
      await fetchMeterReaderOptions()
    }

    fetchOptions()
  }, [officeValue, getValue, type])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const toggleOption = (option: string | OptionItem) => {
    const value = getValue(option)

    if (value === 'all') {
      setSelectedOptions(['all'])
      return
    }

    if (value === 'no') {
      setSelectedOptions(['no'])
      return
    }

    const isSelected = selectedOptions.includes(value)

    const updated = isSelected
      ? selectedOptions.filter((item) => item !== value)
      : [...selectedOptions.filter((item) => item !== 'all' && item !== 'no'), value]

    const allValuesExceptSpecial = optionValues.filter((v) => v !== 'all' && v !== 'no')

    if (updated.filter((v) => v !== 'all' && v !== 'no').length === allValuesExceptSpecial.length) {
      setSelectedOptions(['all'])
    } else if (updated.length === 0) {
      setSelectedOptions(['all'])
    } else {
      setSelectedOptions(updated)
    }
  }

  if (options.length === 0) return null

  return (
    <div className='relative' ref={wrapperRef}>
      <div
        onClick={() => setOpen((prev) => !prev)}
        className='border rounded-md px-4 py-3 bg-white text-lg text-gray-800 w-60 cursor-pointer'
      >
        {(() => {
          const selectedWithoutAll = selectedOptions.filter((v) => v !== 'all')

          const allValuesExceptAll = optionValues.filter((v) => v !== 'all')

          if (
            selectedWithoutAll.length === allValuesExceptAll.length &&
            selectedOptions.includes('all')
          ) {
            return '전체'
          }

          return selectedOptions
            .map((val) => getLabel(options.find((item) => getValue(item) === val) || val))
            .join(', ')
        })()}
      </div>

      {open && (
        <div className='absolute top-full mt-2 border rounded-md bg-white shadow-md z-10 w-60 max-h-80 overflow-auto'>
          {options.map((option) => {
            const value = getValue(option)

            return (
              <label
                key={value}
                className='flex items-center px-4 py-3 text-lg hover:bg-gray-100 cursor-pointer'
              >
                <input
                  type='checkbox'
                  className='mr-2'
                  checked={selectedOptions.includes(value)}
                  onChange={() => toggleOption(option)}
                />

                {getLabel(option)}
              </label>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default MultiSelect
